import type { BillingSummary } from '../app/cloud';
import { el } from './dom';
import { activateModal } from './modal';

export interface UpgradeDialogOptions {
  billing: BillingSummary;
  checkout(): Promise<string>;
  prepareCheckout?(): void | Promise<void>;
}

const row = (feature: string, free: string, pro: string): HTMLElement =>
  el('div', { class: 'plan-row', role: 'row' }, [
    el('strong', { role: 'rowheader' }, [feature]),
    el('span', { role: 'cell' }, [free]),
    el('span', { role: 'cell' }, [pro]),
  ]);

/** Explain the free session before sending anyone to checkout. */
export function openUpgradeDialog(options: UpgradeDialogOptions): () => void {
  document.querySelector('.upgrade-overlay')?.remove();
  const message = el('p', { class: 'onboarding-message', role: 'status' });
  const panel = el(
    'section',
    {
      class: 'upgrade-dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'upgrade-title',
    },
    [
      el('header', { class: 'upgrade-header' }, [
        el('div', {}, [
          el('span', { class: 'onboarding-kicker' }, ['KilnCat Pro']),
          el('h1', { id: 'upgrade-title' }, ['Keep every texture within reach']),
          el('p', {}, [
            'Free sessions include the complete designer and exports. Pro adds durable cloud work and collaboration.',
          ]),
        ]),
        el('button', { class: 'upgrade-close', type: 'button', 'aria-label': 'Close' }, ['×']),
      ]),
      el('div', { class: 'plan-table', role: 'table', 'aria-label': 'Free and Pro comparison' }, [
        el('div', { class: 'plan-row plan-head', role: 'row' }, [
          el('span', { role: 'columnheader' }, ['']),
          el('strong', { role: 'columnheader' }, ['Free']),
          el('strong', { role: 'columnheader' }, ['Pro']),
        ]),
        row('Design, render & export', 'Included', 'Included'),
        row('Starter & gallery templates', 'Included', 'Included'),
        row('Cloud saves', '—', 'Included'),
        row('Cross-device access', '—', 'Included'),
        row('View shared textures', 'Included', 'Included'),
        row('Edit, share & collaborate', '—', 'Included'),
        row('Publish to the CC0 gallery', '—', 'Included'),
      ]),
      el('p', { class: 'upgrade-note' }, [
        'Free work stays in the current tab until you export it. It is not saved in this browser or the cloud.',
      ]),
      message,
      options.billing.enabled
        ? el('div', { class: 'upgrade-checkout' }, [
            el('p', { class: 'upgrade-renewal' }, [
              'Pro is $5 per month and renews monthly until canceled. Cancel through the customer portal before renewal. By continuing, you agree to the ',
              el('a', { href: '/terms', target: '_blank' }, ['Terms']),
              ' and ',
              el('a', { href: '/privacy', target: '_blank' }, ['Privacy Policy']),
              '.',
            ]),
            el(
              'button',
              {
                class: 'btn primary upgrade-action',
                type: 'button',
                onclick: (event: Event) => {
                  const button = event.currentTarget as HTMLButtonElement;
                  button.disabled = true;
                  button.textContent = 'Opening checkout…';
                  void Promise.resolve(options.prepareCheckout?.())
                    .then(() => options.checkout())
                    .then((url) => window.location.assign(url))
                    .catch((error) => {
                      message.textContent = (error as Error).message;
                      button.disabled = false;
                      button.textContent = 'Upgrade to Pro';
                    });
                },
              },
              ['Upgrade to Pro'],
            ),
          ])
        : el('p', { class: 'upgrade-note' }, [
            'Plans are not enabled on this installation. You can still export texture files and maps.',
          ]),
    ],
  );
  const overlay = el('div', { class: 'upgrade-overlay' }, [panel]);
  let deactivate = (): void => {};
  const close = (): void => {
    deactivate();
    overlay.remove();
  };
  panel.querySelector<HTMLButtonElement>('.upgrade-close')!.onclick = close;
  overlay.onpointerdown = (event) => {
    if (event.target === overlay) close();
  };
  document.body.appendChild(overlay);
  deactivate = activateModal(overlay, panel, { onEscape: close });
  return close;
}
